import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	ArrowDownCircle,
	ArrowUpCircle,
	Banknote,
	CreditCard,
	History,
	LayoutDashboard,
	RefreshCw,
	ShoppingCart,
	Wallet,
} from "lucide-react";
import { formatCHF, getSavedReaderId, pos, saveReaderId } from "../../services/posService";
import { ErrorBox, Modal, POSHeader, timeOf, useCurrentSession } from "./posShared";

export default function POSDashboard() {
	const { loading, error, register, session, testMode, reload } = useCurrentSession();

	return (
		<div className="min-h-screen bg-[#F7F3ED] text-[#34251D]">
			<POSHeader
				title="Léger's Coffee"
				subtitle={register ? `${register.name} · Système de caisse` : "Système de caisse"}
				back={null}
				right={
					<button
						type="button"
						onClick={reload}
						className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm hover:bg-white/20"
					>
						<RefreshCw size={16} /> Actualiser
					</button>
				}
			/>

			<main className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
				{testMode ? (
					<div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
						<strong>Mode test Stripe</strong> : les paiements par terminal sont simulés, aucun argent réel n'est
						débité. Les ventes en espèces sont bien enregistrées.
					</div>
				) : null}

				<ErrorBox message={error} />

				{loading && !register ? (
					<p className="rounded-xl bg-white p-5 text-sm">Chargement de la caisse…</p>
				) : null}

				{!loading && register && !session ? <OpenSessionCard onOpened={reload} /> : null}

				{session ? (
					<>
						<SessionSummary session={session} />
						<div className="grid gap-4 md:grid-cols-3">
							<MenuCard to="/pos/checkout" icon={ShoppingCart} title="Nouvelle vente" text="Encaisser au comptoir" primary />
							<MenuCard to="/pos/sales" icon={History} title="Historique des ventes" text="Terminal et espèces, ticket par ticket" />
							<MenuCard to="/pos/closing" icon={Wallet} title="Clôture de caisse" text="Compter le tiroir et fermer la journée" />
						</div>
						<CashMovements session={session} onDone={reload} />
					</>
				) : null}

				<TerminalSettings testMode={testMode} />
			</main>
		</div>
	);
}

function MenuCard({ to, icon: Icon, title, text, primary }) {
	return (
		<Link
			to={to}
			className={`rounded-2xl border p-6 shadow-sm transition hover:shadow-md ${primary ? "border-[#34251D] bg-[#34251D] text-white" : "border-[#E6E1D6] bg-white"}`}
		>
			<Icon size={28} className="mb-4" />
			<h3 className="text-lg font-semibold">{title}</h3>
			<p className={`mt-1 text-sm ${primary ? "text-white/75" : "text-gray-500"}`}>{text}</p>
		</Link>
	);
}

function OpenSessionCard({ onOpened }) {
	const [amount, setAmount] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	async function open(event) {
		event.preventDefault();
		const value = Number(String(amount).replace(",", "."));
		if (!Number.isFinite(value) || value < 0) {
			setError("Saisissez le fond de caisse (0 si vide).");
			return;
		}
		setBusy(true);
		setError("");
		try {
			await pos.openSession(value.toFixed(2));
			onOpened();
		} catch (e) {
			setError(e.message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6">
			<div className="mb-4 flex items-center gap-3">
				<LayoutDashboard size={26} />
				<div>
					<h2 className="text-xl font-bold">Ouvrir la caisse</h2>
					<p className="text-sm text-gray-500">Comptez les espèces présentes dans le tiroir avant la première vente.</p>
				</div>
			</div>
			<form onSubmit={open} className="flex max-w-md flex-col gap-3 sm:flex-row sm:items-end">
				<label className="flex-1">
					<span className="mb-1 block text-sm font-medium">Fond de caisse (CHF)</span>
					<input
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						inputMode="decimal"
						placeholder="200.00"
						className="w-full rounded-xl border p-3 text-lg"
						autoFocus
					/>
				</label>
				<button type="submit" disabled={busy} className="rounded-xl bg-[#34251D] px-6 py-3 font-semibold text-white disabled:opacity-60">
					{busy ? "Ouverture…" : "Ouvrir la caisse"}
				</button>
			</form>
			<div className="mt-3">
				<ErrorBox message={error} />
			</div>
		</section>
	);
}

function SessionSummary({ session }) {
	return (
		<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6">
			<div className="mb-5 flex flex-wrap items-center justify-between gap-2">
				<h2 className="text-xl font-bold">Caisse ouverte depuis {timeOf(session.opened_at)}</h2>
				<span className="text-sm text-gray-500">
					{session.sales_count} vente{session.sales_count > 1 ? "s" : ""}
					{session.awaiting_count ? ` · ${session.awaiting_count} paiement en attente` : ""}
				</span>
			</div>
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				<Tile label="Total encaissé" value={formatCHF(session.sales_total)} strong />
				<Tile
					label="Terminal"
					icon={CreditCard}
					value={formatCHF(session.terminal.total)}
					sub={`${session.terminal.count} paiement${session.terminal.count > 1 ? "s" : ""}`}
				/>
				<Tile
					label="Espèces"
					icon={Banknote}
					value={formatCHF(session.cash.total)}
					sub={`${session.cash.count} paiement${session.cash.count > 1 ? "s" : ""}`}
				/>
				<Tile label="Espèces attendues dans le tiroir" value={formatCHF(session.expected_cash)} sub={`dont fond ${formatCHF(session.opening_cash)}`} />
			</div>
		</section>
	);
}

function Tile({ label, value, sub, icon: Icon, strong }) {
	return (
		<div className={`rounded-xl p-4 ${strong ? "bg-[#34251D] text-white" : "bg-[#F7F3ED]"}`}>
			<p className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${strong ? "text-white/70" : "text-gray-500"}`}>
				{Icon ? <Icon size={14} /> : null} {label}
			</p>
			<p className="mt-1 text-2xl font-bold">{value}</p>
			{sub ? <p className={`text-xs ${strong ? "text-white/70" : "text-gray-500"}`}>{sub}</p> : null}
		</div>
	);
}

function CashMovements({ session, onDone }) {
	const [kind, setKind] = useState(null);
	const [amount, setAmount] = useState("");
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	async function submit(event) {
		event.preventDefault();
		const value = Number(String(amount).replace(",", "."));
		if (!Number.isFinite(value) || value <= 0) return setError("Montant invalide.");
		if (!reason.trim()) return setError("Indiquez le motif.");
		setBusy(true);
		setError("");
		try {
			await pos.cashMovement(session.session_id, kind, value.toFixed(2), reason.trim());
			setKind(null);
			setAmount("");
			setReason("");
			onDone();
		} catch (e) {
			setError(e.message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6">
			<h2 className="text-lg font-bold">Mouvements d'espèces</h2>
			<p className="mt-1 text-sm text-gray-500">
				Entrées {formatCHF(session.cash_in)} · Sorties {formatCHF(session.cash_out)}. À utiliser pour un ajout de monnaie
				ou une dépense payée avec le tiroir.
			</p>
			<div className="mt-4 flex flex-wrap gap-3">
				<button type="button" onClick={() => setKind("cash_in")} className="flex items-center gap-2 rounded-xl border px-4 py-2.5 hover:bg-[#F7F3ED]">
					<ArrowDownCircle size={18} /> Entrée d'espèces
				</button>
				<button type="button" onClick={() => setKind("cash_out")} className="flex items-center gap-2 rounded-xl border px-4 py-2.5 hover:bg-[#F7F3ED]">
					<ArrowUpCircle size={18} /> Sortie d'espèces
				</button>
			</div>
			{kind ? (
				<Modal title={kind === "cash_in" ? "Entrée d'espèces" : "Sortie d'espèces"} onClose={() => setKind(null)}>
					<form onSubmit={submit} className="space-y-4">
						<label className="block">
							<span className="mb-1 block text-sm font-medium">Montant (CHF)</span>
							<input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="w-full rounded-xl border p-3" autoFocus />
						</label>
						<label className="block">
							<span className="mb-1 block text-sm font-medium">Motif</span>
							<input
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								placeholder={kind === "cash_in" ? "Ajout de monnaie" : "Achat de lait"}
								className="w-full rounded-xl border p-3"
							/>
						</label>
						<ErrorBox message={error} />
						<button type="submit" disabled={busy} className="w-full rounded-xl bg-[#34251D] p-3 font-semibold text-white disabled:opacity-60">
							{busy ? "Enregistrement…" : "Enregistrer"}
						</button>
					</form>
				</Modal>
			) : null}
		</section>
	);
}

function TerminalSettings({ testMode }) {
	const [state, setState] = useState({ loading: true, error: "", data: null });
	const [selected, setSelected] = useState(getSavedReaderId());
	const [creating, setCreating] = useState(false);

	const load = useCallback(async () => {
		setState((s) => ({ ...s, loading: true, error: "" }));
		try {
			const data = await pos.readers();
			setState({ loading: false, error: "", data });
			const ids = (data.readers || []).map((r) => r.id);
			const saved = getSavedReaderId();
			if (!saved && ids.length === 1) {
				saveReaderId(ids[0]);
				setSelected(ids[0]);
			}
		} catch (e) {
			setState({ loading: false, error: e.message, data: null });
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	function choose(id) {
		saveReaderId(id);
		setSelected(id);
	}

	async function createSimulated() {
		setCreating(true);
		try {
			const { reader } = await pos.createSimulatedReader();
			choose(reader.id);
			await load();
		} catch (e) {
			setState((s) => ({ ...s, error: e.message }));
		} finally {
			setCreating(false);
		}
	}

	const readers = state.data?.readers || [];

	return (
		<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-3">
					<CreditCard size={24} />
					<div>
						<h2 className="text-lg font-bold">Terminal de paiement</h2>
						<p className="text-sm text-gray-500">Lecteur utilisé par cet appareil pour les paiements par carte.</p>
					</div>
				</div>
				<button type="button" onClick={load} className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#F7F3ED]">
					<RefreshCw size={15} /> Rechercher
				</button>
			</div>

			<div className="mt-4 space-y-2">
				<ErrorBox message={state.error} />
				{state.loading ? <p className="text-sm text-gray-500">Recherche des lecteurs…</p> : null}
				{!state.loading && !state.error && readers.length === 0 ? (
					<p className="text-sm text-gray-600">
						Aucun lecteur enregistré. Ajoutez votre terminal dans le tableau de bord Stripe (Terminal → Lecteurs).
					</p>
				) : null}
				{readers.map((reader) => (
					<label key={reader.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${selected === reader.id ? "border-[#34251D] bg-[#F7F3ED]" : ""}`}>
						<input type="radio" name="reader" checked={selected === reader.id} onChange={() => choose(reader.id)} />
						<div className="min-w-0 flex-1">
							<p className="font-semibold">{reader.label || reader.id}</p>
							<p className="text-xs text-gray-500">
								{reader.device_type} · {reader.serial_number || reader.id}
							</p>
						</div>
						<span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${reader.status === "online" ? "bg-emerald-50 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
							{reader.status === "online" ? "En ligne" : "Hors ligne"}
						</span>
					</label>
				))}
				{testMode ? (
					<button type="button" onClick={createSimulated} disabled={creating} className="mt-2 rounded-xl border border-dashed px-4 py-2.5 text-sm hover:bg-[#F7F3ED] disabled:opacity-60">
						{creating ? "Création…" : "Créer un terminal simulé (mode test)"}
					</button>
				) : null}
			</div>
		</section>
	);
}
